import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Briefcase, ChevronDown, FileText, Loader2, LogIn, Paperclip, Send,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { API, BACKEND_URL } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";
import PageHero from "../components/PageHero";

const STATUS_META = {
  new: { label: "Eingereicht", cls: "bg-sky-500/10 text-sky-300" },
  in_review: { label: "In Prüfung", cls: "bg-amber-500/10 text-amber-300" },
  accepted: { label: "Angenommen", cls: "bg-emerald-500/10 text-emerald-300" },
  rejected: { label: "Abgelehnt", cls: "bg-rose-500/10 text-rose-300" },
};

const fmtDate = (iso) => new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function MyApplications() {
  const { userToken, user } = useAuth();
  const { toast } = useToast();
  const [apps, setApps] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const auth = { headers: { Authorization: `Bearer ${userToken}` } };

  const load = async () => {
    try {
      const r = await axios.get(`${API}/jobs/applications/mine`, auth);
      setApps(r.data || []);
    } catch { setApps([]); }
  };

  useEffect(() => {
    if (userToken) load();
    
  }, [userToken]);

  const sendReply = async (appId) => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/jobs/applications/${appId}/messages`, { text: reply.trim() }, auth);
      setReply("");
      await load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast({ title: "Fehler", description: typeof d === "string" ? d : "Senden fehlgeschlagen", variant: "destructive" });
    } finally { setSending(false); }
  };

  return (
    <section className="mx-auto max-w-3xl px-4 py-14" data-testid="my-applications-page">
      <PageHero
        eyebrow="Bewerbungen"
        icon={FileText}
        title="Meine Bewerbungen"
        intro="Hier siehst du den Status deiner Bewerbungen und kannst dich direkt mit dem Team austauschen."
        testId="my-applications-hero"
      />

      {!userToken ? (
        <div className="mt-8 rounded-2xl border border-white/10 bg-[#0f141b] p-8 text-center" data-testid="my-applications-login-gate">
          <Briefcase className="mx-auto h-8 w-8 text-zinc-600" />
          <div className="mt-3 text-lg font-bold text-white">Bitte melde dich an</div>
          <div className="mt-5 flex justify-center gap-2">
            <Link to="/login"><Button className="btn-primary rounded-lg px-4 py-2 text-sm font-semibold"><LogIn className="mr-1.5 h-4 w-4" /> Einloggen</Button></Link>
          </div>
        </div>
      ) : !apps ? (
        <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>
      ) : apps.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-white/10 bg-[#0f141b] px-4 py-12 text-center" data-testid="my-applications-empty">
          <div className="text-sm text-zinc-500">Du hast dich noch nicht beworben.</div>
          <Link to="/jobs" className="mt-3 inline-block text-sm font-semibold text-[#b39bff] hover:text-white">Offene Jobs ansehen →</Link>
        </div>
      ) : (
        <div className="mt-8 space-y-2">
          {apps.map((a) => {
            const meta = STATUS_META[a.status] || STATUS_META.new;
            const isOpen = expanded === a.id;
            return (
              <div key={a.id} className="overflow-hidden rounded-xl border border-white/10 bg-[#0f141b]" data-testid={`my-application-${a.id}`}>
                <button
                  onClick={() => { setExpanded(isOpen ? null : a.id); setReply(""); }}
                  data-testid={`my-application-toggle-${a.id}`}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.03]"
                >
                  <Briefcase className="h-4 w-4 shrink-0 text-zinc-500" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-white">{a.job_title}</div>
                    <div className="text-[11px] text-zinc-500">{fmtDate(a.created_at)} · {(a.messages || []).length} Nachricht(en)</div>
                  </div>
                  <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${meta.cls}`}>{meta.label}</span>
                  <ChevronDown className={`h-4 w-4 text-zinc-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="border-t border-white/5 px-4 py-4">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Deine Bewerbung</div>
                    <div className="mt-1.5 whitespace-pre-wrap rounded-lg bg-white/5 px-3 py-2.5 text-sm text-zinc-300">{a.message}</div>
                    {a.attachment_url && (
                      <a href={`${BACKEND_URL}${a.attachment_url}`} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-[#b39bff] hover:text-white">
                        <Paperclip className="h-3 w-3" /> {a.attachment_name || "Anhang"}
                      </a>
                    )}

                    <div className="mt-4 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Austausch mit dem Team</div>
                    <div className="mt-2 space-y-2">
                      {(a.messages || []).length === 0 && (
                        <div className="py-3 text-center text-xs text-zinc-600">Noch keine Nachrichten – du kannst dem Team unten schreiben.</div>
                      )}
                      {(a.messages || []).map((m) => (
                        <div key={m.id} className={`rounded-lg px-3 py-2 text-sm ${m.is_team ? "bg-[#7c4dff]/10 text-zinc-200" : "bg-white/5 text-zinc-300"}`}>
                          <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                            {m.is_team ? "Habthek-Team" : (m.author || user?.habbo_name)} · {fmtDate(m.created_at)}
                          </div>
                          <div className="whitespace-pre-wrap">{m.text}</div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 flex gap-2">
                      <Input
                        data-testid={`my-application-reply-input-${a.id}`}
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") sendReply(a.id); }}
                        placeholder="Nachricht an das Team…"
                        className="bg-[#10141b] border-white/10 text-zinc-200"
                      />
                      <Button onClick={() => sendReply(a.id)} disabled={sending || !reply.trim()} data-testid={`my-application-reply-send-${a.id}`} className="btn-primary rounded-lg px-3 py-2 text-sm">
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
